import type {
  AdminEnvelope,
  AdminUserAccount,
  AdminUserConsoleAdminResponse,
  AdminUserCreatePayload,
  AdminUserEraseResponse,
  AdminUserHoldResponse,
  AdminUserUpdatePayload,
  AdminUserBulkPayload,
  AdminUserBulkResult,
  AdminUserExportResult,
  AdminUserSelection,
} from '@/client/types/admin/users-orgs';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import useGetMutationFn from '../use-get-mutation-fn';
import { toFormData } from './lib/form';
import { adminUserKeys } from './users.query';
import { adminRoleKeys } from './roles';

export const useCreateAdminUserMutation = () => {
  const queryClient = useQueryClient();

  const mutationFn = useGetMutationFn<FormData, AdminEnvelope<AdminUserAccount>>({
    defaultErrorMessage: 'An unexpected error occurred while creating the user',
    endpoint: '/api/admin/users',
    method: 'POST',
  });

  return useMutation({
    mutationFn: (payload: AdminUserCreatePayload) =>
      mutationFn(toFormData(payload as unknown as Record<string, unknown>)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminUserKeys.all });
    },
  });
};

/** Verification flag columns the admin can toggle manually. */
export type VerificationField =
  | 'email1_ver'
  | 'email2_ver'
  | 'phone1_ver'
  | 'phone2_ver'
  | 'verified_id'
  | 'verified_photo'
  | 'verified_address'
  | 'age_ver'
  | 'verified_acc'
  | 'verified_acc_historical';

export const useAdminUserVerificationMutation = () => {
  const queryClient = useQueryClient();

  const mutationFn = useGetMutationFn<
    { uid: number } & Partial<Record<VerificationField, 0 | 1>>,
    AdminEnvelope<AdminUserAccount>
  >({
    defaultErrorMessage: 'An unexpected error occurred while updating verification',
    endpoint: (payload) => `/api/admin/users/${payload.uid}`,
    method: 'PUT',
  });

  return useMutation({
    // Sends the real column, e.g. `{ uid, email1_ver: 1 }`.
    mutationFn: ({ uid, field, value }: { uid: number; field: VerificationField; value: 0 | 1 }) =>
      mutationFn({ uid, [field]: value }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminUserKeys.all });
    },
  });
};

/** Every verification flag an admin controls — the set cleared by "un-verify all". */
export const VERIFICATION_FIELDS: VerificationField[] = [
  'email1_ver',
  'email2_ver',
  'phone1_ver',
  'phone2_ver',
  'verified_id',
  'verified_photo',
  'verified_address',
  'age_ver',
  'verified_acc',
  'verified_acc_historical',
];

export const useAdminUserUnverifyAllMutation = () => {
  const queryClient = useQueryClient();

  const mutationFn = useGetMutationFn<
    { uid: number } & Partial<Record<VerificationField, 0>>,
    AdminEnvelope<AdminUserAccount>
  >({
    defaultErrorMessage: 'An unexpected error occurred while clearing verification',
    endpoint: (payload) => `/api/admin/users/${payload.uid}`,
    method: 'PUT',
  });

  return useMutation({
    mutationFn: ({ uid }: { uid: number }) =>
      mutationFn({
        uid,
        ...(Object.fromEntries(
          VERIFICATION_FIELDS.map((field) => [field, 0]),
        ) as Record<VerificationField, 0>),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminUserKeys.all });
    },
  });
};

export const useUpdateAdminUserMutation = () => {
  const queryClient = useQueryClient();

  const mutationFn = useGetMutationFn<
    AdminUserUpdatePayload,
    AdminEnvelope<AdminUserAccount>
  >({
    defaultErrorMessage: 'An unexpected error occurred while updating the user',
    endpoint: (payload) => `/api/admin/users/${payload.uid}`,
    method: 'PUT',
  });

  return useMutation({
    mutationFn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminUserKeys.all });
    },
  });
};

export const useAdminUserHoldMutation = () => {
  const queryClient = useQueryClient();

  const setHold = useGetMutationFn<
    { uid: number; reason?: string },
    AdminEnvelope<AdminUserHoldResponse>
  >({
    defaultErrorMessage: 'An unexpected error occurred while suspending the user',
    endpoint: (payload) => `/api/admin/users/${payload.uid}/hold`,
    method: 'PUT',
  });

  const liftHold = useGetMutationFn<
    { uid: number; reason?: string },
    AdminEnvelope<AdminUserHoldResponse>
  >({
    defaultErrorMessage: 'An unexpected error occurred while reactivating the user',
    endpoint: (payload) => `/api/admin/users/${payload.uid}/hold`,
    method: 'DELETE',
  });

  return useMutation({
    mutationFn: ({
      uid,
      hold,
      reason,
    }: {
      uid: number;
      hold: boolean;
      reason?: string;
    }) => (hold ? setHold({ uid, reason }) : liftHold({ uid, reason })),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminUserKeys.all });
    },
  });
};

/** Console access (`user_account.admin`). Gated on `system.admin.manage`;
 *  the generic user PUT refuses `admin` as a protected field (ff-api#882). */
export const useAdminUserConsoleAdminMutation = () => {
  const queryClient = useQueryClient();

  const grant = useGetMutationFn<
    { uid: number; reason?: string },
    AdminEnvelope<AdminUserConsoleAdminResponse>
  >({
    defaultErrorMessage: 'An unexpected error occurred while granting console access',
    endpoint: (payload) => `/api/admin/users/${payload.uid}/admin`,
    method: 'PUT',
  });

  const revoke = useGetMutationFn<
    { uid: number; reason?: string },
    AdminEnvelope<AdminUserConsoleAdminResponse>
  >({
    defaultErrorMessage: 'An unexpected error occurred while revoking console access',
    endpoint: (payload) => `/api/admin/users/${payload.uid}/admin`,
    method: 'DELETE',
  });

  return useMutation({
    mutationFn: ({
      uid,
      admin,
      reason,
    }: {
      uid: number;
      admin: boolean;
      reason?: string;
    }) => (admin ? grant({ uid, reason }) : revoke({ uid, reason })),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminUserKeys.all });
    },
  });
};

/** Staff status (`user_account.employee`). Gated on `system.employee.manage`.
 *  Removing it 409s while the account still has console access. */
export const useAdminUserStaffMutation = () => {
  const queryClient = useQueryClient();

  const grant = useGetMutationFn<
    { uid: number; reason?: string },
    AdminEnvelope<{ uid: number; authid: string; employee: number }>
  >({
    defaultErrorMessage: 'An unexpected error occurred while marking the account as staff',
    endpoint: (payload) => `/api/admin/users/${payload.uid}/employee`,
    method: 'PUT',
  });

  const revoke = useGetMutationFn<
    { uid: number; reason?: string },
    AdminEnvelope<{ uid: number; authid: string; employee: number }>
  >({
    defaultErrorMessage: 'An unexpected error occurred while removing staff status',
    endpoint: (payload) => `/api/admin/users/${payload.uid}/employee`,
    method: 'DELETE',
  });

  return useMutation({
    mutationFn: ({ uid, staff, reason }: { uid: number; staff: boolean; reason?: string }) =>
      staff ? grant({ uid, reason }) : revoke({ uid, reason }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminUserKeys.all });
    },
  });
};

export const useAdminUserEraseMutation = () => {
  const queryClient = useQueryClient();

  const mutationFn = useGetMutationFn<
    { uid: number; confirm: true; reason?: string },
    AdminEnvelope<AdminUserEraseResponse>
  >({
    defaultErrorMessage: 'An unexpected error occurred while erasing the user',
    endpoint: (payload) => `/api/admin/users/${payload.uid}/erase`,
    method: 'POST',
  });

  return useMutation({
    mutationFn: ({ uid, reason }: { uid: number; reason?: string }) =>
      mutationFn({ uid, confirm: true, reason }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminUserKeys.all });
    },
  });
};

export const useRevokeSessionMutation = () => {
  const queryClient = useQueryClient();

  const mutationFn = useGetMutationFn<
    { authid: string; sid: string },
    AdminEnvelope<{ success: string; authid: string; revoked_sid: string }>
  >({
    defaultErrorMessage: 'An unexpected error occurred while revoking the session',
    endpoint: (payload) =>
      `/api/admin/users/sessions/${payload.authid}/${payload.sid}`,
    method: 'DELETE',
  });

  return useMutation({
    mutationFn,
    onSuccess: (_result, variables) => {
      queryClient.invalidateQueries({
        queryKey: adminUserKeys.sessions(variables.authid),
      });
    },
  });
};

/** `POST /api/admin/users/sessions/<authid>/revoke-all`. */
export const useRevokeAllSessionsMutation = () => {
  const queryClient = useQueryClient();

  const mutationFn = useGetMutationFn<
    { authid: string },
    AdminEnvelope<{ success: string; authid: string; revoked_count: number }>
  >({
    defaultErrorMessage: 'An unexpected error occurred while revoking sessions',
    endpoint: (payload) =>
      `/api/admin/users/sessions/${payload.authid}/revoke-all`,
    method: 'POST',
  });

  return useMutation({
    mutationFn,
    onSuccess: (_result, variables) => {
      queryClient.invalidateQueries({
        queryKey: adminUserKeys.sessions(variables.authid),
      });
    },
  });
};

/** Bulk/export responses arrive in the `{status, data}` envelope; accept either. */
const unwrapEnvelope = <T,>(raw: unknown): T =>
  ((raw as { data?: T })?.data ?? raw) as T;

/** `POST /api/admin/users/bulk` — one action over uids or "all matching". */
export const useAdminUsersBulkMutation = () => {
  const queryClient = useQueryClient();

  const mutationFn = useGetMutationFn<AdminUserBulkPayload, unknown>({
    defaultErrorMessage: 'An unexpected error occurred while updating users',
    endpoint: '/api/admin/users/bulk',
    method: 'POST',
  });

  return useMutation({
    mutationFn: async (payload: AdminUserBulkPayload) =>
      unwrapEnvelope<AdminUserBulkResult>(await mutationFn(payload)),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: adminUserKeys.all });
      queryClient.invalidateQueries({ queryKey: adminRoleKeys.all });
    },
  });
};

/** `POST /api/admin/users/export` — CSV text for uids or "all matching". */
export const useAdminUsersExportMutation = () => {
  const mutationFn = useGetMutationFn<AdminUserSelection, unknown>({
    defaultErrorMessage: 'An unexpected error occurred while exporting users',
    endpoint: '/api/admin/users/export',
    method: 'POST',
  });

  return useMutation({
    mutationFn: async (selection: AdminUserSelection) =>
      unwrapEnvelope<AdminUserExportResult>(await mutationFn(selection)),
  });
};

/** `DELETE /api/admin/users/<uid>/mfa` — turn two-step verification off for a
 *  user who lost their phone or mailbox. A reason is required; the user is
 *  emailed. */
export const useAdminUserMfaResetMutation = () => {
  const queryClient = useQueryClient();

  const mutationFn = useGetMutationFn<
    { uid: number; reason: string },
    AdminEnvelope<{ uid: number; enabled: boolean; was: string | null }>
  >({
    defaultErrorMessage: 'An unexpected error occurred while resetting two-step verification',
    endpoint: (payload) => `/api/admin/users/${payload.uid}/mfa`,
    method: 'DELETE',
  });

  return useMutation({
    mutationFn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminUserKeys.all });
    },
  });
};
